import React, { useCallback, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import { alpha, useTheme } from "@mui/material/styles";
import { RichText } from "./RichText";

export interface BannerData {
  id: string;
  title: string;
  description?: string | null;
  image?: { url: string; alt?: string | null } | null;
}

export interface BannerProps {
  banner: BannerData;
}

// Inline SVG icons to avoid external icon dependencies
const ChevronUpIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const STORAGE_KEY = "banner-expanded";

function getStoredExpanded(id: string): boolean {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return true;
    const map = JSON.parse(stored);
    return map[id] !== false;
  } catch {
    return true;
  }
}

function setStoredExpanded(id: string, value: boolean) {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    const map = stored ? JSON.parse(stored) : {};
    map[id] = value;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore storage errors
  }
}

export function Banner({ banner }: BannerProps) {
  const [expanded, setExpanded] = useState(() => getStoredExpanded(banner.id));

  const toggle = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      setStoredExpanded(banner.id, next);
      return next;
    });
  }, [banner.id]);
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: alpha(theme.palette.info.main, 0.08),
        borderBottom: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
      }}
      data-label="Banner"
    >
      {/* Collapsed bar — always visible */}
      <Box
        sx={{
          maxWidth: 960,
          mx: "auto",
          display: "flex",
          alignItems: "center",
          px: 2,
          py: expanded ? 0 : 0.5,
          minHeight: 36,
          cursor: "pointer",
        }}
        onClick={toggle}
        data-label="BannerHeader"
      >
        <Box
          sx={{
            flex: 1,
            fontWeight: 600,
            fontSize: "0.85rem",
            color: theme.palette.info.dark,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {banner.title}
        </Box>

        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          sx={{ color: theme.palette.info.dark }}
          aria-label={expanded ? "Collapse banner" : "Expand banner"}
        >
          {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </IconButton>
      </Box>

      {/* Expanded content */}
      <Collapse in={expanded}>
        <Box
          sx={{
            maxWidth: 960,
            mx: "auto",
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            px: 2,
            pb: 1.5,
          }}
          data-label="BannerContent"
        >
          {banner.image?.url && (
            <Box
              component="img"
              src={banner.image.url}
              alt={banner.image.alt || banner.title}
              sx={{
                width: 80,
                height: 80,
                objectFit: "cover",
                borderRadius: 1,
                flexShrink: 0,
              }}
            />
          )}
          <Box
            sx={{
              flex: 1,
              fontSize: "0.875rem",
              color: theme.palette.text.primary,
              "& p, & div": { margin: "0.25rem 0" },
            }}
          >
            <RichText value={banner.description} />
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
